import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from 'mobx-react';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import './goalsPane.module.scss';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { routingStore } from '../../../../common/stores/routing';
import { V3Routes } from '../../../routes';
import { observable } from 'mobx';
import { GoalsPaneState } from './states/GoalsPaneState';
import { GlobalLoader } from '../../../../mainv2/components/GlobalLoader';
import moment from 'moment-es6';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { BackButtonHeader } from './BackButtonHeader';
import { Plus } from './images/Plus';
import { Edit } from './images/Edit';
import { ChevronLeft, ChevronRight } from './images/Chevron';
import { ISODateTime } from '../../../../commonv3/types/dates';
import { OverlayPage } from '../../../../commonv3/components/molecules/layout/OverlayPage';
import { IdealCareerPane } from './IdealCareerPane';
import { SheModal } from '../../../../commonv3/components/molecules/layout/SheModal';
import { YearlyGoalPane } from './YearlyGoalPane';

type MonthSelectorDirection = 'left' | 'right';

type MonthSelectorButtonProps = {
    active: boolean;
    onClick?: () => void;
    direction: MonthSelectorDirection;
};

const MonthSelectorButton = ({ active, onClick, direction }: MonthSelectorButtonProps) => {
    if (active) {
        return (
            <div styleName="arrow active" onClick={onClick}>
                {direction === 'left' ? <ChevronLeft color="white" /> : <ChevronRight color="white" />}
            </div>
        );
    } else {
        return (
            <div styleName="arrow inactive">
                {direction === 'left' ? <ChevronLeft color="gray" /> : <ChevronRight color="gray" />}
            </div>
        );
    }
};

type ThumbnailSectionProps = {
    updatedAt?: ISODateTime;
    url?: string;
    onClick?: () => void;
    title: string;
};

const ThumbnailSection = ({ title, updatedAt, url, onClick }: ThumbnailSectionProps) => {
    return (
        <div styleName="section">
            <div>
                <div styleName="text">{title}</div>
                <div styleName="upload-date">
                    {updatedAt ? (
                        <span>
                            {moment(updatedAt).format('YYYY/MM/DD')}
                            アップロード
                        </span>
                    ) : (
                        <span>シートをアップロード</span>
                    )}
                </div>
            </div>
            <div>
                {url ? (
                    <img src={url} styleName="thumb-image" onClick={onClick} />
                ) : (
                    <Button styleName="button button-normal" type="normal" size="xs" onClick={onClick}>
                        <Plus styleName="button-icon" color="gray" />
                        <span styleName="button-text">追加する</span>
                    </Button>
                )}
            </div>
        </div>
    );
};

type Props = {
    homeState: HomePageState;
};

@inject('homeState')
@observer
export class GoalsPane extends React.Component<Props> {
    @observable pageState = new GoalsPaneState();

    componentDidMount(): void {
        this.pageState.init();
    }

    render() {
        const {
            monthlyGoal,
            longTermGoal,
            year,
            month,
            firstMonth,
            lastMonth,
            showIdealCareerModal,
            showYearlyGoalModal,
        } = this.pageState;
        if (longTermGoal.loading) {
            return <GlobalLoader />;
        }
        const monthly = monthlyGoal.getOrElse(null);
        const longTerm = longTermGoal.getOrThrow();
        return (
            <div styleName="pane">
                {GlobalStatesV3.viewport.isSp && <BackButtonHeader to={V3Routes.home()} />}
                <div styleName="header">
                    <div styleName="h1">GOAL SETTING</div>
                    <div styleName="h2">目標管理</div>
                </div>
                <div styleName="ideal-career">
                    <ThumbnailSection
                        updatedAt={longTerm.ideal_career_updated_at}
                        url={longTerm.ideal_career_url}
                        onClick={() =>
                            GlobalStatesV3.viewport.isSp
                                ? this.pageState.openIdealCareerModal()
                                : routingStore.push(V3Routes.idealCareer())
                        }
                        title="理想のキャリア"
                    />
                </div>
                <div styleName="yearly-goal">
                    <ThumbnailSection
                        updatedAt={longTerm.yearly_goal_updated_at}
                        url={longTerm.yearly_goal_url}
                        onClick={() =>
                            GlobalStatesV3.viewport.isSp
                                ? this.pageState.openYearlyGoalModal()
                                : routingStore.push(V3Routes.yearlyGoal())
                        }
                        title="1年間の目標"
                    />
                </div>
                <div styleName="monthly-goal">
                    <div styleName="section">
                        <div styleName="month-selector">
                            <div styleName="text">{this.pageState.monthText()}の目標</div>
                            <MonthSelectorButton
                                active={firstMonth && (year !== firstMonth.year || month !== firstMonth.month)}
                                onClick={() => this.pageState.selectPreviousMonth()}
                                direction="left"
                            />
                            <MonthSelectorButton
                                active={lastMonth && (year !== lastMonth.year || month !== lastMonth.month)}
                                onClick={() => this.pageState.selectNextMonth()}
                                direction="right"
                            />
                        </div>
                        <div>
                            <Button
                                styleName="button button-primary"
                                type="primary"
                                size="xs"
                                onClick={() => routingStore.push(V3Routes.monthlyGoal(year, month))}
                            >
                                <Edit styleName="button-icon" />
                                <span styleName="button-text">編集する</span>
                            </Button>
                        </div>
                    </div>
                    <div styleName="content">
                        <div styleName="content-section">
                            <div styleName="h3">今月の目標</div>
                            <div>
                                <pre>{monthly && monthly.goal}</pre>
                            </div>
                        </div>
                        <div styleName="content-section">
                            <div styleName="h3">今月やること</div>
                            <ul>
                                {monthly &&
                                    monthly.todos &&
                                    monthly.todos.map((todo, i) => (
                                        <li key={i}>
                                            <div styleName="bullet" />
                                            <div styleName="item">{todo}</div>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                        <div styleName="content-section">
                            <div styleName="h3">達成度</div>
                            <div styleName="progress-wrapper">
                                <div styleName="progress">{(monthly && monthly.progress) || 0}</div>
                                <div styleName="percent">%</div>
                            </div>
                        </div>
                        <div styleName="content-section">
                            <div styleName="h3">自己評価</div>
                            <div>
                                <pre>{monthly && monthly.reviews}</pre>
                            </div>
                        </div>
                        <div styleName="content-section">
                            <div styleName="h3">ネクストアクション</div>
                            <ul>
                                {monthly &&
                                    monthly.next_actions &&
                                    monthly.next_actions.map((nextAction, i) => (
                                        <li key={i}>
                                            <div styleName="bullet" />
                                            <div styleName="item">{nextAction}</div>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    </div>
                </div>
                <SheModal open={showIdealCareerModal} onRequestClose={this.pageState.closeIdealCareerModal}>
                    <div styleName="modal-body">
                        <IdealCareerPane homeState={this.props.homeState} />
                    </div>
                </SheModal>
                <SheModal open={showYearlyGoalModal} onRequestClose={this.pageState.closeYearlyGoalModal}>
                    <div styleName="modal-body">
                        <YearlyGoalPane homeState={this.props.homeState} />
                    </div>
                </SheModal>
            </div>
        );
    }
}
